import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { fetchVerificationEstimate, RECHECK_LABEL, type BatchDetail, type VerifyKind } from './api';
import { leadImportKeys } from './keys';
import { fmtInt } from './ui';

const KINDS: { kind: VerifyKind; label: string; provider: string }[] = [
  { kind: 'phone', label: 'Verify main phones', provider: 'Veriphone: mobile, landline or invalid' },
  { kind: 'email', label: 'Verify emails', provider: 'MillionVerifier: deliverable or not' },
];

/**
 * [spec B1] The Import confirm. Phone and email checks start on; only new
 * leads' values without a stored answer cost a credit, unless the operator
 * includes leads already in the CRM (RECHECK_LABEL). A failed check never
 * blocks a lead.
 */
export default function ImportConfirm({ batch, leads, pending, onImport, onClose }: {
  batch: BatchDetail; leads: number; pending: boolean;
  onImport: (verify: VerifyKind[], recheck: boolean) => void; onClose: () => void;
}) {
  const [verify, setVerify] = useState<VerifyKind[]>(['phone', 'email']);
  const [recheck, setRecheck] = useState(false);
  const estimate = useQuery({
    queryKey: leadImportKeys.verifyEstimate(batch.id, batch.preparation_generation),
    queryFn: () => fetchVerificationEstimate(batch.id),
  });
  const e = estimate.data;
  const credits = (k: VerifyKind) => (verify.includes(k) && e ? (recheck ? e[k].recheck : e[k].new) : 0);
  const phones = credits('phone');
  const emails = credits('email');
  const toggle = (k: VerifyKind, on: boolean) =>
    setVerify((v) => (on ? [...v, k] : v.filter((x) => x !== k)).sort() as VerifyKind[]);

  const summary = !verify.length
    ? 'No verification. You can still verify leads later from the import.'
    : !e
      ? 'Counting what needs a check…'
      : phones + emails === 0
        ? 'Nothing new to check. Verification uses no credits.'
        : `Verification will use credits for ${[
          verify.includes('email') ? `${fmtInt(emails)} ${emails === 1 ? 'email' : 'emails'}` : null,
          verify.includes('phone') ? `${fmtInt(phones)} ${phones === 1 ? 'phone' : 'phones'}` : null,
        ].filter(Boolean).join(' and ')}. Continue?`;

  return (
    <Dialog isOpen onClose={() => { if (!pending) onClose(); }} title={`Import ${fmtInt(leads)} leads`} style={{ width: 540 }}>
      <DialogBody>
        <div className="li-stack" aria-label="Verification on import">
          {KINDS.map(({ kind, label, provider }) => (
            <div key={kind} className="li-verify-opt">
              <Checkbox checked={verify.includes(kind)} disabled={pending} label={label}
                onChange={(ev) => toggle(kind, (ev.target as HTMLInputElement).checked)} />
              <span className="li-muted">
                {provider}.
                {e && <> {fmtInt(e[kind].values)} in the file: <b>{fmtInt(e[kind].new)} new</b>
                  {e[kind].known > 0 && `, ${fmtInt(e[kind].known)} from leads already in the CRM`}
                  {e[kind].stored > 0 && `, ${fmtInt(e[kind].stored)} already verified`}
                  {e[kind].waiting > 0 && `, ${fmtInt(e[kind].waiting)} being checked`}.</>}
              </span>
            </div>
          ))}
          {verify.length > 0 && (
            <Checkbox checked={recheck} disabled={pending} label={RECHECK_LABEL}
              onChange={(ev) => setRecheck((ev.target as HTMLInputElement).checked)} />
          )}
          {estimate.isError && <p className="li-muted">The credit estimate could not be loaded: {(estimate.error as Error).message}</p>}
          <p data-testid="li-import-credits"><b>{summary}</b></p>
          <p className="li-muted">Checks run after the import finishes. A failed check does not hold back any lead.</p>
        </div>
      </DialogBody>
      <DialogFooter actions={<>
        <Button disabled={pending} onClick={onClose}>Cancel</Button>
        <Button intent="primary" loading={pending} onClick={() => onImport(verify, recheck && verify.length > 0)}>
          {verify.length ? 'Import and verify' : 'Import'}
        </Button>
      </>} />
    </Dialog>
  );
}
